import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Warehouse } from 'lucide-react';
import type { WarehouseToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useWarehousesList, useWarehouseMutations, useWarehouseTypes } from '~/hooks/use-warehouses';
import { tokenToForm, type WarehouseFormValues } from '~/lib/schemas/warehouse';
import { WarehouseEditDialog } from './warehouse-edit-dialog';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Warehouses (Αποθήκες) CRUD surface — parity with the legacy admin grid (Code/Name/Type/Disabled/Visible/Sort). */
export function WarehousesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useWarehousesList();
	const { data: types } = useWarehouseTypes();
	const { remove } = useWarehouseMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<WarehouseFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<WarehouseToken | null>(null);

	const typeName = useMemo(() => {
		const map = new Map<number, string>();
		for (const ty of types ?? []) if (ty.id != null) map.set(ty.id, ty.nm ?? '');
		return (id?: number) => (id != null ? (map.get(id) ?? '') : '');
	}, [types]);

	const columns = useMemo<SystemGridColumn<WarehouseToken>[]>(
		() => [
			{
				key: 'code',
				header: t('catalog.warehouses.columns.code'),
				width: '160px',
				sortValue: o => o.cd ?? '',
				exportValue: o => o.cd ?? '',
				render: o => <span className="truncate font-mono text-xs text-foreground-muted">{o.cd || '—'}</span>
			},
			{
				key: 'name',
				header: t('catalog.warehouses.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'type',
				header: t('catalog.warehouses.columns.type'),
				width: '180px',
				sortValue: o => typeName(o.wt),
				exportValue: o => typeName(o.wt),
				render: o => <span className="truncate text-sm text-foreground-muted">{typeName(o.wt) || '—'}</span>
			},
			{
				key: 'disabled',
				header: t('catalog.warehouses.columns.disabled'),
				width: '110px',
				align: 'center',
				sortValue: o => (o.is_disabled ? 1 : 0),
				exportValue: o => (o.is_disabled ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.is_disabled)} />
			},
			{
				key: 'visible',
				header: t('catalog.warehouses.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.iv)} />
			},
			{
				key: 'sort',
				header: t('catalog.warehouses.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: o => o.si ?? 0,
				exportValue: o => o.si ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.si}</span>
			}
		],
		[t, typeName]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<WarehouseToken>
				gridId="warehouses"
				title={t('catalog.warehouses.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.nm}
				columns={columns}
				searchPredicate={(o, q) => (o.nm ?? '').toLowerCase().includes(q) || (o.cd ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Warehouse size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('catalog.warehouses.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="warehouses"
			/>

			<WarehouseEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.warehouses.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('catalog.warehouses.deleteBody', { name: deleteRow?.nm || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
